import { StructuredJsonFieldDef } from '../../../shared/design-system/structured-json-form.model';

export const GROWTH_PAID_MEDIA_PROVIDERS = ['yektanet', 'google-ads'] as const;
export const GROWTH_PAID_MEDIA_NATIONS = ['IR', 'US', 'GLOBAL'] as const;
export const GROWTH_PAID_MEDIA_OBJECTIVES = ['awareness', 'traffic', 'conversions', 'retargeting'] as const;
export const GROWTH_PAID_MEDIA_APPROVAL_STATES = ['Draft', 'PendingApproval', 'Approved', 'Blocked'] as const;
export const GROWTH_PAID_MEDIA_SYNC_STATUSES = [
  'Pending',
  'Completed',
  'SkippedNoConsent',
  'Failed',
  'IdempotentReplay'
] as const;

export type GrowthPaidMediaProviderKey = (typeof GROWTH_PAID_MEDIA_PROVIDERS)[number];
export type GrowthPaidMediaNationCode = (typeof GROWTH_PAID_MEDIA_NATIONS)[number];

export interface GrowthPaidMediaAccountDto {
  id: string;
  providerKey: string;
  connectorKey: string;
  authConfigRef: string;
  nationCode: string;
  isEnabled: boolean;
  canBeEnabled: boolean;
  killSwitchEnabled: boolean;
  killSwitchReason: string | null;
  capabilitiesJson: string;
}

export interface GrowthPaidMediaCampaignLinkDto {
  id: string;
  accountId: string;
  growthCampaignId: string;
  externalCampaignId: string;
  objective: string;
  budgetCap: number;
  approvalState: string;
  status: string;
  approvalReason: string | null;
  requestedSpend: number | null;
}

export interface GrowthPaidMediaAudienceSyncDto {
  id: string;
  accountId: string;
  segmentId: string;
  externalAudienceId: string | null;
  consentPurposeKey: string;
  hashedMemberCount: number;
  status: string;
  idempotencyKey: string;
}

export interface GrowthPaidMediaSpendSnapshotDto {
  id: string;
  campaignLinkId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
}

export interface GrowthPaidMediaRoasDto {
  id: string;
  campaignLinkId: string;
  periodStartUtc: string;
  periodEndUtc: string;
  spend: number;
  revenue: number;
  roas: number;
}

export interface CreatePaidMediaAccountRequest {
  providerKey: string;
  authConfigRef?: string | null;
  nationCode?: string | null;
}

export interface SetPaidMediaKillSwitchRequest {
  enabled: boolean;
  reason?: string | null;
}

export interface CreatePaidMediaCampaignLinkRequest {
  accountId: string;
  growthCampaignId: string;
  externalCampaignId?: string | null;
  objective?: string | null;
  budgetCap: number;
}

export interface SubmitPaidMediaCampaignLinkRequest {
  requestedSpend: number;
}

export interface ApprovePaidMediaCampaignLinkRequest {
  reason?: string | null;
}

export interface SyncPaidMediaAudienceRequest {
  accountId: string;
  segmentId: string;
  consentPurposeKey: string;
  idempotencyKey: string;
}

export const growthPaidMediaCapabilitiesFields: StructuredJsonFieldDef[] = [
  { key: 'audienceSync', type: 'boolean', labelKey: 'growth.paidMedia.capabilities.fields.audienceSync' },
  { key: 'conversionUpload', type: 'boolean', labelKey: 'growth.paidMedia.capabilities.fields.conversionUpload' },
  { key: 'spendPull', type: 'boolean', labelKey: 'growth.paidMedia.capabilities.fields.spendPull' },
  { key: 'campaignLink', type: 'boolean', labelKey: 'growth.paidMedia.capabilities.fields.campaignLink' },
  { key: 'mode', type: 'string', labelKey: 'growth.paidMedia.capabilities.fields.mode' }
];

export function isIranResidency(nationCode: string | null | undefined): boolean {
  const code = (nationCode ?? '').trim().toUpperCase();
  return code === 'IR' || code === 'IRN';
}

export function accountDisplayLabel(account: GrowthPaidMediaAccountDto): string {
  return `${account.providerKey} (${account.nationCode})`;
}

export function campaignLinkDisplayLabel(
  link: GrowthPaidMediaCampaignLinkDto,
  campaignName?: string | null
): string {
  const name = campaignName?.trim() || link.externalCampaignId || link.objective;
  return `${name} · ${link.approvalState}`;
}
